import { t as ct } from "../lib/content.js";
import { HomeworkReview } from "./HomeworkReview.jsx";
import { resolveSchoolYear } from "../lib/age.js";
import { childHasSameList } from "../lib/homework.js";

const { useState } = React;

// LB-382 (Alan, 27 Sep). A list another family shared, arriving by link.
//
// Two screens, and the second one is not new. WHICH CHILD comes first, because a list only means
// anything attached to one — and the year it was written for sits beside each child's own, so a
// Year 6 sheet going to a Year 3 is a visible choice rather than an accident. Nothing is blocked
// on it: a parent knows their own child better than a label does.
//
// Then HomeworkReview, exactly as a sheet the child sent goes through it: the same words, the
// same red rude block, the same practise-until date, the same LB-377 picker when the grid and
// the drafts are both full. That reuse IS the answer to LB-380 — a list arriving from a stranger
// is checked on the way in, not trusted because it came from a link.
//
// "No thanks" discards it and does nothing else. Nothing is recorded, and nothing goes back to
// whoever shared it. It is offered where there is something to decide (LB-382 receive fixes,
// Alan, 1 Oct: "Surely these make more sense once a child has been selected?"): on the review,
// as its decline; on the no-children screen, where it is the only way out; and on the
// already-has-it card. On the which-child screen only when a child already has it (Alan, 2 Oct:
// otherwise it stayed in Requests for good); with nobody holding it that screen is left by its
// own back (Requests) or close (the overlay).
//
// A child who already has this list — live, draft or on the shelf (childHasSameList) — cannot be
// given it again: a second copy takes another of their five live slots and splits their
// progress. They show "Already has this list" and are not tappable; when nobody is left to give
// it to, a short card says so instead of the review.
// The props are defaulted because this screen is registered by component in cms-screens.mjs —
// it has no route into the app, so the snapshot renders it with nothing passed, and a screen
// that throws on missing props is a screen whose copy cannot be edited.
// `onClose` (Alan, 28 Sep): Decide later on the review, which leaves the list waiting in
// Requests. It is NOT onDiscard: the first cut wired the review's close to the discard, so a
// parent who stepped away lost the list.
// Alan, 28 Sep: with ONE child there is nothing to choose, so the review opens straight away
// with the words on it; with several, the words are shown under the question rather than hidden
// behind a child's name.
export function HomeworkReceive({ state = {}, shared = {}, onAdd = () => {}, onDiscard = () => {}, onClose = null }) {
  const profiles = state.profiles || [];
  const [childId, setChildId] = useState(profiles.length === 1 ? profiles[0].id : null);
  const year = String(shared.schoolYear || "").trim();
  const words = shared.pairs || [];
  const listType = shared.type || "spelling";
  const hasIt = (p) => childHasSameList(state.learningContent && state.learningContent[p.id], listType, words);

  if (!profiles.length) {
    return (
      <div className="bg-white rounded-lg p-4" data-testid="hw-receive-no-children">
        <div className="text-sm font-extrabold text-slate-900">{shared.name || ct("hwreceive.a_list", "Homework")}</div>
        <p className="text-sm text-slate-500 mt-2">{ct("hwreceive.no_children", "Add a child first and the link will still work.")}</p>
        <button onClick={onDiscard} className="w-full mt-3 bg-white border border-slate-200 text-slate-900 text-sm font-bold rounded-lg py-3">{ct("hwreceive.discard", "No thanks")}</button>
      </div>
    );
  }

  // Nobody left to give it to: one child who has it, or every child has it.
  if (profiles.every(hasIt)) {
    const only = profiles.length === 1 ? profiles[0] : null;
    return (
      <div className="bg-white rounded-lg p-4" data-testid="hw-receive-duplicate">
        <div className="text-sm font-extrabold text-slate-900">{shared.name || ct("hwreceive.a_list", "Homework")}</div>
        <div className="text-sm font-bold text-slate-900 mt-2">{only ? ct("hwreceive.dup_one", "{name} already has this homework", { name: only.name }) : ct("hwreceive.dup_all", "Your children already have this homework")}</div>
        <p className="text-sm text-slate-500 mt-1">{ct("hwreceive.dup_body", "It is on their homework already, or on the shelf to switch back on.")}</p>
        <button onClick={onDiscard} className="w-full mt-3 bg-white border border-slate-200 text-slate-900 text-sm font-bold rounded-lg py-3" data-testid="hw-receive-discard">{ct("hwreceive.discard", "No thanks")}</button>
      </div>
    );
  }

  if (!childId) {
    return (
      <div className="space-y-3" data-testid="hw-receive-pick">
        <div className="bg-white rounded-lg p-4">
          <div className="text-sm font-extrabold text-slate-900">{shared.name}</div>
          <div className="text-xs text-slate-500 mt-1">{[
            ct("hwreceive.n_words", "{n} words", { n: words.length }),
            year ? ct("hwreceive.written_for", "written for {year}", { year }) : "",
            ct("hwreceive.from_another", "shared by another family"),
          ].filter(Boolean).join(" · ")}</div>
        </div>
        <div className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400 px-1">{ct("hwreceive.who_for", "Who is it for?")}</div>
        <div className="bg-white rounded-lg overflow-hidden">
          {profiles.map((p) => {
            const theirs = resolveSchoolYear(p) || "";
            const has = hasIt(p);
            return (
              <button key={p.id} onClick={() => setChildId(p.id)} disabled={has} className="w-full text-left px-4 py-3 border-b border-slate-100 last:border-b-0 disabled:cursor-default" data-testid={`hw-receive-child-${p.id}`}>
                <span className="flex items-center justify-between gap-2">
                  <span className={`text-sm font-bold ${has ? "text-slate-400" : "text-slate-900"}`}>{p.name}</span>
                  {has
                    ? <span className="text-[11px] font-bold text-slate-400" data-testid={`hw-receive-has-${p.id}`}>{ct("hwreceive.already_has", "Already has this homework")}</span>
                    : theirs && <span className={`text-[11px] font-bold ${year && theirs === year ? "text-slate-900" : "text-slate-500"}`}>{theirs}</span>}
                </span>
              </button>
            );
          })}
        </div>
        {/* Alan, 2 Oct: with one child already holding it, the ones left may not want it either,
            and there was no way to say so here; it sat in Requests for good. So when anyone has
            it, the screen offers the decline itself, under the children it is about. With
            nobody holding it the 1 Oct rule stands: the decline is on the review, after a child. */}
        {profiles.some(hasIt) && (
          <button onClick={onDiscard} className="w-full bg-white border border-slate-200 text-slate-900 text-sm font-bold rounded-lg py-3" data-testid="hw-receive-discard">{ct("hwreceive.discard", "No thanks")}</button>
        )}
        <div className="bg-white rounded-lg overflow-hidden" data-testid="hw-receive-words">
          <div className="px-4 py-2 text-[11px] font-extrabold uppercase tracking-wide text-slate-400 border-b border-slate-100">{ct("hwreceive.the_words", "The words")}</div>
          {words.map((p, i) => (
            <div key={i} className="px-4 py-2 text-sm text-slate-900 border-b border-slate-100 last:border-b-0">{p.b ? <>{p.a} <span className="text-slate-400">·</span> <span className="font-bold">{p.b}</span></> : p.a}</div>
          ))}
        </div>
      </div>
    );
  }

  // The same review a sheet from the child goes through — words, rude block, date, picker.
  return (
    <div className="space-y-2">
      {profiles.length > 1 && <button onClick={() => setChildId(null)} className="text-xs font-bold text-slate-900 underline" data-testid="hw-receive-back">{ct("hwreceive.back", "Someone else")}</button>}
      <HomeworkReview
        state={state}
        childId={childId}
        req={{ pairs: shared.pairs, droppedCount: 0 }}
        listType={shared.type || "spelling"}
        lang={shared.lang || ""}
        direction={shared.direction || "en2lang"}
        title={ct("hwreceive.review_title", "Homework for {name}", { name: (profiles.find((p) => p.id === childId) || {}).name || "" })}
        onApprove={(pairs, until, archiveFirst, extra) => onAdd(childId, { name: (extra && extra.name) || shared.name, type: shared.type, lang: (extra && extra.lang) || shared.lang || "", direction: (extra && extra.direction) || shared.direction || "en2lang", pairs, oinksOn: extra ? extra.oinksOn !== false : true, finishTokens: (extra && extra.finishTokens) || 0 }, until, archiveFirst)}
        initialName={shared.name || ""}
        declineMode="decline"
        onReject={onDiscard}
        onClose={onClose || onDiscard}
      />
    </div>
  );
}

// CarriedHomeworkPrompt (LB-382, 1 Oct) lived here: the homework a family signed up with, told
// once in Parent Mode. Folded into the waiting-requests overlay on 2 Oct (Alan), where that
// homework is a row with "Tried before you signed up"; see RequestsOverlay.jsx.
